import { useEffect, useState, useCallback } from "react";
import { useAccountingStore } from "../../../store/Accounting";
import { AkontoViewType } from "@/lib/supabase/supabaseTypes";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/dentlab/src/components/ui/table";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/dentlab/src/components/ui/card";
import { Button } from "@/dentlab/src/components/ui/button";
import { IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowUpRightFromSquare, Plus, Info, RotateCcw } from "lucide-react";
import { CreateAkontoInvoiceDialog } from "./components/create-akonto-invoice-dialog.component";
import { PayoutAkontoDialog } from "./components/payout-akonto-dialog.component";
import {
	useSheetStore,
	SheetContentType,
} from "@/dentlab/src/store/Sheets/sheet.store";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { useCentralStore } from "../../../store/Central";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";

const recipientType = (row: AkontoViewType) => {
	return row.recipient_client_id
		? "Kunde"
		: row.recipient_guarantor_id
			? "Garant"
			: "Patient";
};

const formatCurrency = (amount?: number, currency = "CHF") => {
	if (amount === undefined || amount === null) return "--";
	return `${amount.toFixed(2)} ${currency}`;
};

export const AkontoPage: React.FC = () => {
	const [loading, setLoading] = useState<boolean>(true);
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const [payoutTarget, setPayoutTarget] = useState<AkontoViewType | null>(
		null
	);
	const navigate = useNavigate();
	const organizationId = useCentralStore((s) => s.organization?.id);

	const { akontoData, fetchAkontoData } = useAccountingStore((state) => ({
		akontoData: state.akontoData,
		fetchAkontoData: state.fetchAkontoData,
	}));

	const { openSheet } = useSheetStore((state) => ({
		openSheet: state.openSheet,
	}));

	const { getPatient } = usePatientStore((state) => ({
		getPatient: state.getPatient,
	}));

	const loadData = useCallback(async () => {
		setLoading(true);
		await fetchAkontoData();
		setLoading(false);
	}, [fetchAkontoData]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const handleSuccess = () => {
		loadData();
	};

	const handleRowClick = async (akonto: AkontoViewType) => {
		if (akonto.recipient_patient_id) {
			const patient = await getPatient(akonto.recipient_patient_id);
			if (patient) {
				openSheet(SheetContentType.PATIENT, { patient });
			}
		}
	};

	return (
		<div className="py-6">
			<div className="px-6 flex justify-center">
				<Card className="w-fit">
					<CardHeader>
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-2">
								<TooltipWrapper title="Die Ansicht zeigt alle offenen Akonto-Salden.">
									<button
										type="button"
										className="text-gray-400 hover:text-gray-600 p-1"
										aria-label="Zweck anzeigen"
									>
										<Info className="h-4 w-4" />
									</button>
								</TooltipWrapper>
								<CardTitle>Akonto-Übersicht</CardTitle>
							</div>
							<Button
								onClick={() => setDialogOpen(true)}
								size="sm"
								data-testid="create-akonto-invoice-button"
								aria-label="Akontorechnung erstellen"
							>
								<Plus className="h-4 w-4 mr-2" />
								Akontorechnung erstellen
							</Button>
						</div>
					</CardHeader>
					<CardContent>
						{loading ? (
							<div className="flex justify-center items-center py-8">
								<div className="text-gray-500">
									Lade Akonto-Daten...
								</div>
							</div>
						) : (
							<div className="flex justify-center">
								<Table className="w-auto">
									<TableHeader>
										<TableRow>
											<TableHead className="w-60">
												Empfänger
											</TableHead>
											<TableHead className="text-right w-32">
												Akonto Betrag
											</TableHead>
											<TableHead className="w-20">
												Aktionen
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{akontoData.length === 0 ? (
											<TableRow>
												<TableCell
													colSpan={3}
													className="text-center py-8"
												>
													<div className="text-gray-500">
														Keine Akonto-Einträge
														gefunden
													</div>
												</TableCell>
											</TableRow>
										) : (
											akontoData.map(
												(akonto: AkontoViewType) => (
													<TableRow
														key={
															akonto.akonto_invoice_id
														}
														className={`group hover:bg-gray-50 transition-colors ${
															akonto.recipient_patient_id
																? "cursor-pointer"
																: ""
														}`}
														onClick={() =>
															handleRowClick(
																akonto
															)
														}
													>
														<TableCell className="font-medium">
															{
																akonto.recipient_full_name
															}
															{` (${recipientType(
																akonto
															)})`}
														</TableCell>
														<TableCell className="text-right font-mono">
															{formatCurrency(
																akonto.outstanding_amount ??
																	0
															)}
														</TableCell>
														<TableCell>
															<div className="flex items-center justify-end w-full">
																{/* Fixed width container for consistent alignment */}
																<div className="flex items-center gap-1 min-w-[60px] justify-end">
																	{/* Hover-visible primary actions */}
																	<div className="flex gap-1 transition-opacity">
																		<Tooltip title="Guthaben auszahlen">
																			<IconButton
																				size="small"
																				onClick={(
																					e
																				) => {
																					e.stopPropagation();
																					setPayoutTarget(
																						akonto
																					);
																				}}
																				aria-label="Guthaben auszahlen"
																				data-testid="payout-akonto-button"
																			>
																				<RotateCcw className="h-4 w-4" />
																			</IconButton>
																		</Tooltip>
																		<Tooltip title="Zahlungshistorie">
																			<IconButton
																				size="small"
																				onClick={(
																					e
																				) => {
																					e.stopPropagation();
																					navigate(
																						`/debitoren/zahlungsjournal?recipient=${
																							akonto.recipient_client_id ||
																							akonto.recipient_guarantor_id ||
																							akonto.recipient_patient_id
																						}`
																					);
																				}}
																			>
																				<ArrowUpRightFromSquare className="h-4 w-4" />
																			</IconButton>
																		</Tooltip>
																	</div>
																</div>
															</div>
														</TableCell>
													</TableRow>
												)
											)
										)}
									</TableBody>
								</Table>
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			<CreateAkontoInvoiceDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				onSuccess={handleSuccess}
			/>

			{payoutTarget && organizationId && (
				<PayoutAkontoDialog
					open={!!payoutTarget}
					onOpenChange={(open) => {
						if (!open) setPayoutTarget(null);
					}}
					onSuccess={() => {
						setPayoutTarget(null);
						loadData();
					}}
					organizationId={organizationId}
					recipientName={payoutTarget.recipient_full_name || ""}
					recipientClientId={payoutTarget.recipient_client_id ?? null}
					recipientGuarantorId={
						payoutTarget.recipient_guarantor_id ?? null
					}
					recipientPatientId={
						payoutTarget.recipient_patient_id ?? null
					}
					outstandingAmount={payoutTarget.outstanding_amount ?? 0}
					currency="CHF"
				/>
			)}
		</div>
	);
};
